/**
 * Review Panel Component
 *
 * Displays verification results, a diff viewer, and a compact decision bar
 * for approving, requesting changes, or rejecting task execution results.
 */

import { useState } from 'react';
import { Check, MessageSquare, Undo2, X, Loader2 } from 'lucide-react';
import { DiffViewer } from './DiffViewer';
import { TaskPreview, VerificationResult } from '../../hooks/useTaskExecution';

interface ReviewPanelProps {
  preview: TaskPreview | null;
  verification: VerificationResult | null;
  onSubmitReview: (decision: 'approve' | 'reject' | 'request_changes', feedback?: string) => void;
  // Return to the question step to revise answers. Optional — omit to hide
  // the "Revise Answers" action.
  onReviseAnswers?: () => void;
  loading?: boolean;
}

export function ReviewPanel({
  preview,
  verification,
  onSubmitReview,
  onReviseAnswers,
  loading = false,
}: ReviewPanelProps) {
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  const handleApprove = () => {
    onSubmitReview('approve');
  };

  const handleRequestChanges = () => {
    if (!showFeedback) {
      setShowFeedback(true);
      return;
    }

    if (!feedback.trim()) {
      return;
    }

    onSubmitReview('request_changes', feedback);
  };

  const handleReject = () => {
    onSubmitReview('reject');
  };

  const handleCancelFeedback = () => {
    setShowFeedback(false);
    setFeedback('');
  };

  return (
    <div className="space-y-6">
      {/* Verification Results */}
      {verification && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Verification Results
          </h3>

          <div className="space-y-3">
            {/* Overall Status */}
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  verification.overall_success
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}
              >
                {verification.overall_success ? (
                  <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
                ) : (
                  <X className="w-6 h-6 text-red-600 dark:text-red-400" />
                )}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {verification.overall_success ? 'All Checks Passed' : 'Some Checks Failed'}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {verification.overall_success
                    ? 'Changes verified successfully'
                    : 'Please review the errors below'}
                </p>
              </div>
            </div>

            {/* Individual Checks */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {verification.tests_passed != null && (
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      verification.tests_passed ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Tests {verification.tests_passed ? 'Passed' : 'Failed'}
                  </span>
                </div>
              )}
              {verification.build_passed != null && (
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      verification.build_passed ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Build {verification.build_passed ? 'Passed' : 'Failed'}
                  </span>
                </div>
              )}
              {verification.lint_passed != null && (
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      verification.lint_passed ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Lint {verification.lint_passed ? 'Passed' : 'Failed'}
                  </span>
                </div>
              )}
            </div>

            {/* Errors */}
            {verification.errors && verification.errors.length > 0 && (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <h5 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-2">
                  Errors:
                </h5>
                <ul className="list-disc list-inside space-y-1">
                  {verification.errors.map((error, idx) => (
                    <li key={idx} className="text-sm text-red-800 dark:text-red-300">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Changes Preview + Decision Bar (single card) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {preview && <DiffViewer diff={preview.diff} files={preview.files} embedded />}

        {/* Feedback textarea — expands inline above the action buttons */}
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ maxHeight: showFeedback ? '300px' : '0' }}
        >
          <div className="px-4 pt-4 pb-2">
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              What changes are needed?
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Describe what you'd like changed..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-none"
              disabled={loading}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
          {/* Tertiary: Reject (de-emphasized) */}
          <div>
            <button
              type="button"
              onClick={handleReject}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-500/40 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-400 dark:hover:border-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-4 h-4" />
              Reject
            </button>
          </div>

          {/* Primary + Secondary actions grouped to the right */}
          <div className="flex items-center gap-3">
            {showFeedback && (
              <button
                type="button"
                onClick={handleCancelFeedback}
                disabled={loading}
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            )}

            {/* Secondary: Revise Answers */}
            {onReviseAnswers && !showFeedback && (
              <button
                type="button"
                onClick={onReviseAnswers}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:focus:ring-offset-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Undo2 className="w-4 h-4" />
                Revise Plan
              </button>
            )}

            {/* Secondary: Request Changes */}
            <button
              type="button"
              onClick={handleRequestChanges}
              disabled={loading || (showFeedback && !feedback.trim())}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:focus:ring-offset-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageSquare className="w-4 h-4" />
              {showFeedback ? 'Submit Feedback' : 'Request Changes'}
            </button>

            {/* Primary: Approve */}
            <button
              type="button"
              onClick={handleApprove}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-500 rounded-lg shadow-xs focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:focus:ring-green-400 dark:focus:ring-offset-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Approve & Create PR
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
